import { Component, Input, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { CustomFieldDefinition, CustomFieldType, ValidationRules } from '../../../models/custom-field.model';

@Component({
  selector: 'app-dynamic-field',
  templateUrl: './dynamic-field.component.html',
  styleUrls: ['./dynamic-field.component.scss'],
})
export class DynamicFieldComponent implements OnInit {
  @Input() field!: CustomFieldDefinition;
  @Input() externalFormControl!: FormControl; // Optional input from parent

  fieldType = CustomFieldType;
  validationRules: ValidationRules = {};
  options: any[] = [];
  formControl!: FormControl; // Will always be initialized in ngOnInit

  ngOnInit(): void {
    this.initializeFormControl();
    this.parseValidationRules();
    this.parseOptions();
  }

  /**
   * Initialize FormControl - either use parent's or create a new one
   */
  private initializeFormControl(): void {
    const validators = this.buildValidators();

    if (this.externalFormControl) {
      // Parent provided a formControl, append validators to it
      this.formControl = this.externalFormControl;
      const existingValidators = this.formControl.validator;
      const combinedValidators = existingValidators ? [existingValidators, ...validators] : validators;
      this.formControl.setValidators(combinedValidators);
      this.formControl.updateValueAndValidity({ emitEvent: false });
      
      // Set read-only state if defined in field
      if (this.field.IsReadOnly) {
        this.formControl.disable({ emitEvent: false });
      }
    } else {
      // No parent control provided, create a new one
      this.formControl = new FormControl(
        { value: '', disabled: this.field.IsReadOnly },
        validators
      );
    }
  }

  private buildValidators(): any[] {
    const validators: any[] = [];

    if (this.field.ValidationRules) {
      try {
        const rules = JSON.parse(this.field.ValidationRules);

        if (rules.required) {
          validators.push(Validators.required);
        }
        if (rules.minLength) {
          validators.push(Validators.minLength(rules.minLength));
        }
        if (rules.maxLength) {
          validators.push(Validators.maxLength(rules.maxLength));
        }
        if (rules.min !== undefined) {
          validators.push(Validators.min(rules.min));
        }
        if (rules.max !== undefined) {
          validators.push(Validators.max(rules.max));
        }
        if (rules.pattern) {
          validators.push(Validators.pattern(rules.pattern));
        }
        if (rules.minDate) {
          validators.push(this.minDateValidator(rules.minDate));
        }
        if (rules.maxDate) {
          validators.push(this.maxDateValidator(rules.maxDate));
        }
      } catch (e) {
        console.error('Error parsing validation rules:', e);
      }
    }

    // Add field type specific validators
    switch (this.field.FieldType) {
      case CustomFieldType.Email:
        validators.push(Validators.email);
        break;
      case CustomFieldType.Url:
        validators.push(Validators.pattern('https?://.+'));
        break;
      case CustomFieldType.Phone:
        // Basic phone pattern - can be customized
        validators.push(Validators.pattern('^[0-9\\-\\+\\(\\)\\s]+$'));
        break;
    }

    return validators;
  }

  private minDateValidator(minDate: string): any {
    return (control: FormControl) => {
      if (!control.value) {
        return null;
      }
      const controlDate = new Date(control.value);
      const minDateObj = new Date(minDate);
      return controlDate >= minDateObj ? null : { minDate: true };
    };
  }

  private maxDateValidator(maxDate: string): any {
    return (control: FormControl) => {
      if (!control.value) {
        return null;
      }
      const controlDate = new Date(control.value);
      const maxDateObj = new Date(maxDate);
      return controlDate <= maxDateObj ? null : { maxDate: true };
    };
  }

  private parseValidationRules(): void {
    if (this.field.ValidationRules) {
      try {
        this.validationRules = JSON.parse(this.field.ValidationRules);
      } catch (e) {
        console.warn('Failed to parse validation rules:', e);
        this.validationRules = {};
      }
    }
  }

  private parseOptions(): void {
    if (!this.field.Options) {
      return;
    }

    try {
      // Try to parse as JSON first
      const parsed = JSON.parse(this.field.Options);
      if (Array.isArray(parsed)) {
        this.options = parsed.map((option) =>
          typeof option === 'string' ? { label: option, value: option } : option
        );
      } else {
        this.options = [{ label: parsed, value: parsed }];
      }
    } catch (e) {
      // If JSON parsing fails, treat as comma-separated values
      this.options = this.field.Options.split(',').map((opt) => {
        const trimmed = opt.trim();
        return { label: trimmed, value: trimmed };
      });
    }
  }

  hasError(errorType: string): boolean {
    return !!(this.formControl && this.formControl.hasError(errorType) && (this.formControl.dirty || this.formControl.touched));
  }

  getErrorMessage(): string {
    if (!this.formControl) return '';

    if (this.formControl.hasError('required')) {
      return `${this.field.DisplayLabel} is required`;
    }
    if (this.formControl.hasError('minlength')) {
      return `${this.field.DisplayLabel} must be at least ${this.validationRules.minLength} characters`;
    }
    if (this.formControl.hasError('maxlength')) {
      return `${this.field.DisplayLabel} cannot exceed ${this.validationRules.maxLength} characters`;
    }
    if (this.formControl.hasError('min')) {
      return `${this.field.DisplayLabel} must be at least ${this.validationRules.min}`;
    }
    if (this.formControl.hasError('max')) {
      return `${this.field.DisplayLabel} cannot exceed ${this.validationRules.max}`;
    }
    if (this.formControl.hasError('pattern')) {
      return `${this.field.DisplayLabel} format is invalid`;
    }
    if (this.formControl.hasError('email')) {
      return `${this.field.DisplayLabel} must be a valid email`;
    }
    if (this.formControl.hasError('minDate')) {
      return `${this.field.DisplayLabel} cannot be before ${this.validationRules.minDate}`;
    }
    if (this.formControl.hasError('maxDate')) {
      return `${this.field.DisplayLabel} cannot be after ${this.validationRules.maxDate}`;
    }

    return 'Invalid input';
  }
}
